import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { RefreshCw, Home, TrendingDown, TrendingUp, ArrowRightLeft, CheckCircle2, AlertCircle, Zap, Clock } from 'lucide-react';

const KIND_META = {
  price_drop_sale: { icon: TrendingDown, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/25', label: 'Bajada VENTA' },
  price_up_sale: { icon: TrendingUp, color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/25', label: 'Subida VENTA' },
  price_drop_rent: { icon: TrendingDown, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/25', label: 'Bajada ARRIENDO' },
  price_up_rent: { icon: TrendingUp, color: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/25', label: 'Subida ARRIENDO' },
  status_change: { icon: ArrowRightLeft, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/25', label: 'Estado' },
  availability_change: { icon: ArrowRightLeft, color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/25', label: 'Disponibilidad' },
  new_property: { icon: Home, color: 'text-[#A38560]', bg: 'bg-[#A38560]/10', border: 'border-[#A38560]/25', label: 'Nueva' },
};

function formatCol(iso) {
  if (!iso) return '—';
  try { return new Intl.DateTimeFormat('es-CO', { timeZone: 'America/Bogota', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(iso)); }
  catch { return iso; }
}

export default function WasiSync() {
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [filterKind, setFilterKind] = useState('');

  const load = useCallback(async () => {
    try {
      const [s, ev] = await Promise.all([api.get('/wasi/status'), api.get('/wasi/events?limit=200')]);
      setStatus(s); setEvents(ev);
    } catch { toast.error('Error cargando datos WASI'); }
  }, []);

  useEffect(() => { load(); const iv = setInterval(load, 60000); return () => clearInterval(iv); }, [load]);

  const runSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post('/wasi/sync');
      toast.success(`Sincronización OK: ${r.properties_scanned} propiedades, ${r.events_detected} cambios detectados`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al sincronizar'); }
    setSyncing(false);
  };

  const filtered = filterKind ? events.filter(e => e.kind === filterKind) : events;
  const kinds = [...new Set(events.map(e => e.kind))];

  return (
    <div data-testid="wasi-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-outfit text-2xl lg:text-3xl font-medium text-white flex items-center gap-3">
            <Zap size={24} className="text-[#A38560]" /> Sincronización WASI
          </h1>
          <p className="text-sm text-white/50 mt-1">Detecta automáticamente cambios en tus propiedades: precio, estado y disponibilidad.</p>
        </div>
        <button onClick={runSync} disabled={syncing} data-testid="wasi-sync-btn"
          className="flex items-center gap-2 bg-gradient-to-r from-[#A38560] to-[#8B6F47] text-[#03110D] font-semibold px-4 py-2.5 rounded-xl text-sm disabled:opacity-50 transition-all">
          <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Sincronizando…' : 'Sincronizar ahora'}
        </button>
      </div>

      {/* Status cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-[#0A1F1A] border border-white/5 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-1.5">
            {status?.configured ? <CheckCircle2 size={14} className="text-emerald-400" /> : <AlertCircle size={14} className="text-red-400" />}
            <p className="text-[10px] uppercase tracking-widest text-white/40">Conexión</p>
          </div>
          <p className={`text-lg font-medium ${status?.configured ? 'text-emerald-400' : 'text-red-400'}`} data-testid="wasi-config-status">
            {status?.configured ? 'Configurada' : 'No configurada'}
          </p>
          {status?.id_company && <p className="text-[10px] text-white/30 mt-1">Empresa: {status.id_company}</p>}
        </div>
        <div className="bg-[#0A1F1A] border border-white/5 rounded-2xl p-4">
          <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1.5">Propiedades rastreadas</p>
          <p className="text-2xl font-outfit text-white tabular-nums" data-testid="wasi-total-props">{status?.properties_tracked ?? 0}</p>
        </div>
        <div className="bg-[#0A1F1A] border border-white/5 rounded-2xl p-4">
          <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1.5">Eventos totales</p>
          <p className="text-2xl font-outfit text-[#A38560] tabular-nums" data-testid="wasi-total-events">{status?.total_events ?? 0}</p>
        </div>
        <div className="bg-[#0A1F1A] border border-white/5 rounded-2xl p-4">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Clock size={12} className="text-white/40" />
            <p className="text-[10px] uppercase tracking-widest text-white/40">Última sincronización</p>
          </div>
          <p className="text-sm text-white" data-testid="wasi-last-run">{formatCol(status?.last_run?.run_at)}</p>
          {status?.last_run && (
            <p className="text-[10px] text-white/30 mt-1">
              {status.last_run.triggered_by} · {status.last_run.events_detected || 0} cambio(s)
            </p>
          )}
        </div>
      </div>

      {/* Frequency notice */}
      <div className="bg-[#0A1F1A]/60 border border-white/5 rounded-xl p-3 text-xs text-white/50 flex items-center gap-2">
        <Clock size={12} className="text-[#A38560]" />
        Sincronización automática programada: <span className="text-white">1 vez al día a las 09:00 (Colombia)</span>. Puedes ejecutarla manualmente cuando quieras con el botón de arriba.
      </div>

      {/* Filter chips */}
      {kinds.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="wasi-filter-chips">
          <button onClick={() => setFilterKind('')} data-testid="wasi-filter-all"
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${!filterKind ? 'bg-[#A38560] text-[#03110D]' : 'bg-white/5 text-white/60 hover:text-white'}`}>
            Todos ({events.length})
          </button>
          {kinds.map(k => {
            const meta = KIND_META[k] || { label: k, color: 'text-white/60' };
            const n = events.filter(e => e.kind === k).length;
            return (
              <button key={k} onClick={() => setFilterKind(k)} data-testid={`wasi-filter-${k}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filterKind === k ? 'bg-[#A38560] text-[#03110D]' : `bg-white/5 hover:bg-white/10 ${meta.color}`}`}>
                {meta.label} ({n})
              </button>
            );
          })}
        </div>
      )}

      {/* Events list */}
      <div className="bg-[#0A1F1A] border border-white/5 rounded-2xl overflow-hidden" data-testid="wasi-events">
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <Zap size={40} className="text-white/10 mx-auto mb-3" />
            <p className="text-white/40 text-sm">
              {events.length === 0 ? 'Aún no hay cambios detectados.' : 'No hay eventos con este filtro.'}
            </p>
            <p className="text-white/25 text-xs mt-1">
              {events.length === 0 && (status?.properties_tracked ?? 0) > 0
                ? 'Los cambios aparecerán aquí después de la próxima sincronización.'
                : ''}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {filtered.map(e => {
              const meta = KIND_META[e.kind] || { icon: Zap, color: 'text-white/70', bg: 'bg-white/5', border: 'border-white/10', label: e.kind };
              const Icon = meta.icon;
              const hasBeforeAfter = e.kind !== 'new_property' && (e.old_value || e.new_value);
              return (
                <div key={e.id} data-testid={`wasi-event-${e.id}`} className="p-4 hover:bg-white/[0.02] transition-colors">
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-xl ${meta.bg} border ${meta.border} flex items-center justify-center shrink-0`}>
                      <Icon size={16} className={meta.color} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <p className="text-sm text-white font-medium truncate">{e.title || '(sin título)'}</p>
                        <span className={`text-[9px] uppercase tracking-wider ${meta.color} font-semibold`}>{meta.label}</span>
                      </div>
                      <p className={`text-sm mt-0.5 ${meta.color}`}>{e.message}</p>
                      {hasBeforeAfter && (
                        <div data-testid={`wasi-event-diff-${e.id}`} className="mt-2.5 grid grid-cols-2 gap-2 max-w-md">
                          <div className="bg-white/[0.03] border border-white/5 rounded-lg px-3 py-2">
                            <p className="text-[9px] uppercase tracking-widest text-white/40 mb-0.5">Antes</p>
                            <p className="text-sm text-white/70 font-medium break-words">{e.old_value || '—'}</p>
                          </div>
                          <div className={`${meta.bg} ${meta.border} border rounded-lg px-3 py-2`}>
                            <p className={`text-[9px] uppercase tracking-widest ${meta.color} mb-0.5 opacity-80`}>Ahora</p>
                            <p className={`text-sm ${meta.color} font-semibold break-words`}>{e.new_value || '—'}</p>
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-3 mt-1.5 text-[10px] text-white/40">
                        <span>{formatCol(e.detected_at)}</span>
                        {e.user_name && <span>· Broker: <span className="text-white/60">{e.user_name}</span></span>}
                        {e.id_property && <span>· ID: {e.id_property}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
